// Route guard for every Creative Strategist page (Task #2375).
//
// Two switches decide whether the feature is open, and BOTH are enforced here
// so a closed feature never renders a page at all:
//
//  1. the per-brand `enableCreativeStrategist` flag (mirrored client-side from
//     the brand record — authoritative for masters too, no bypass), and
//  2. the master-only global mode, which only the SERVER knows. The status
//     route is gated exactly like every other Creative Strategist API, so a
//     403 from it means "flag off or Hard Stop" — either way the user is sent
//     back to the brand overview instead of landing on a page whose every
//     request fails.
//
// Redirecting (rather than rendering an error shell) is what makes the
// rollback promise true: with the flag off or the switch in Hard Stop there is
// no Creative Strategist surface to reach, deep links included.

import { Redirect, useParams } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { getQueryFn } from "@/lib/queryClient";
import {
  CREATIVE_STRATEGIST_BRAND_FLAG,
  csStatusKey,
  type CreativeStrategistStatus,
} from "@/lib/creative-strategist";
import type { Brand } from "@shared/schema";

// The global switch is an incident control: notice a flip without a reload,
// but don't hammer the endpoint.
const STATUS_STALE_MS = 15_000;
const STATUS_POLL_MS = 60_000;

/**
 * Is the feature open for this brand right now? Answers "unknown" while the
 * gated status route is still in flight, so callers can fail closed instead of
 * flashing a surface the switch has closed. Shares one cache entry with the
 * guard, so the sidebar costs no extra request.
 */
export function useCreativeStrategistOpen(brandId: string | undefined, flagOn: boolean) {
  const { data, isPending, isError } = useQuery<CreativeStrategistStatus>({
    queryKey: [csStatusKey(brandId)],
    // Explicit rather than inherited: a 401/403 here must reach the caller as
    // an error so it can fail closed.
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!brandId && flagOn,
    retry: false,
    staleTime: STATUS_STALE_MS,
    refetchInterval: STATUS_POLL_MS,
  });

  return {
    isOpen: data?.enabled === true,
    // An error is a definitive "closed" (403 = Hard Stop or flag off), not a
    // pending answer.
    isUnknown: isPending && !isError,
    mode: data?.mode,
  };
}

export function CreativeStrategistGuard({ children }: { children: React.ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const { brandId } = useParams<{ brandId?: string }>();

  // `isPending`, not `isLoading`: on the very first render an enabled query is
  // pending but not yet fetching, and `isLoading` is false there — reading the
  // flag that early would bounce a cold deep-link load off the page before the
  // brand ever arrives.
  const { data: brand, isPending: brandPending } = useQuery<Brand>({
    queryKey: ["/api/brands", brandId],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: !!brandId && !!user,
  });

  const settings = (brand?.settings || {}) as Record<string, boolean>;
  const flagOn = !!settings[CREATIVE_STRATEGIST_BRAND_FLAG];

  const { isOpen, isUnknown } = useCreativeStrategistOpen(brandId, !!user && flagOn);

  if (authLoading) return null;
  if (!brandId) return <Redirect to="/" />;
  if (brandPending) return null;

  // Brand flag off → the feature does not exist for this brand.
  if (!flagOn) return <Redirect to={`/brands/${brandId}`} />;

  if (isUnknown) return null;

  // A 403 here is Hard Stop (or a flag flipped off under us). Anything else
  // that leaves the status unresolved is treated the same way: fail closed.
  if (!isOpen) return <Redirect to={`/brands/${brandId}`} />;

  return <>{children}</>;
}
